/**
 * Sim tunables. Feel lives here — reshape the game without touching the sim.
 *
 * The numbers are chosen so a raid reads as *toy* physics: high gravity, short
 * hops, and launches that fling the figure most of the way across a 12-cell
 * room. Comedy needs distance and airtime; realism needs neither.
 */
export const SIM = {
  /** Fixed step. Never varies — replays and trap timing both depend on it. */
  dt: 1 / 60,
  /** Max steps per frame, so a backgrounded tab cannot spiral. */
  maxStepsPerFrame: 5,
  gravity: -24,

  raider: {
    radius: 0.26,
    halfHeight: 0.3,
    /** Resting body-centre height on a flat floor (halfHeight + radius). */
    standY: 0.56,
    walkSpeed: 4.2,
    /** Slower with the chest: the loot is what makes the exit tense. */
    carrySpeed: 3.1,
    accel: 26,
    airAccel: 7,
    jumpSpeed: 6.4,
    linearDamping: 0.25,
    /** Below this horizontal speed a flung raider scrambles back to their feet. */
    recoverSpeed: 2.4,
    /**
     * Steering authority on normal floor, 0..1-ish. This is *not* physical
     * friction (the capsule has none, on purpose): it is how fast the raider
     * can pull their velocity toward what they asked for. Marbles lower it, so
     * momentum survives and steering does not.
     */
    groundGrip: 0.55,
    /** Per-step horizontal decay while skidding after a launch. */
    skidDecay: 0.94,
  },

  pickupRadius: 0.62,
  escapeRadius: 0.75,
  decoyRadius: 0.6,

  /** Standing still this long counts as visible hesitation. */
  hesitateSeconds: 0.55,
  /** A given trap will not fire again within this window. */
  trapCooldown: 0.7,
  /** Below this Y the raider has fallen through a hole. */
  fallY: -1.8,
  /** Falling in a hole while carrying costs the chest, not the run. */
  pitfallDropsChest: true,

  wallHeight: 2.6,
} as const

/**
 * Shared sweep phase for timed props. The sim and the renderer both call this,
 * so the arm you see is exactly the arm that hits you.
 * Returns -1..1; the arm crosses the centre of its cell when the value is 0.
 */
export function sweepOffset(time: number, period: number): number {
  return Math.sin((time / period) * Math.PI * 2)
}
